/**
 * @file OrderChainGroup.tsx
 * @description Свёртка однотипных узлов «Пути заказа»
 * (phase19.24-order-card-chain-design.md §8, строка «Много однотипных узлов»).
 *
 * Три платежа подряд превращают путь в простыню и прячут в ней задание сборки.
 * Поэтому от двух узлов с одним `groupKey` рисуем одну строку-сводку —
 * «Платежи (3) · 4 820 ₽ · 16.09 10:02», — которая раскрывается тапом.
 *
 * Свёрнуто по умолчанию: раскрытие — осознанное действие менеджера, а не
 * состояние «как оставил прошлый заказ».
 */

import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { OrderChainNode } from "./OrderChainNode";
import type { OrderChainNodeDto } from "../../../lib/order-chain";
import { groupSummary, groupTitle } from "../../../lib/order-chain-node-view";
import { cn } from "@/lib/utils";

interface Props {
  nodes: OrderChainNodeDto[];
  /** Адрес узла — считает панель: она знает слаг и заказ. */
  hrefOf: (node: OrderChainNodeDto) => string | null;
  onRetry?: () => void;
}

export function OrderChainGroup({ nodes, hrefOf, onRetry }: Props) {
  const [open, setOpen] = useState(false);
  const first = nodes[0];
  if (!first) return null;

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        // Тач-таргет ≥56px на телефоне — как у обычного узла пути.
        className="flex min-h-14 w-full items-center gap-3 rounded-xl bg-surface-lowest px-3 py-2.5 text-left transition-colors hover:bg-surface-low lg:min-h-12 lg:py-2"
      >
        <span aria-hidden className="size-3 shrink-0 rounded-full bg-primary/60" />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm font-medium">
            {groupTitle(first.key)} ({nodes.length})
          </span>
          <span className="truncate text-xs text-on-surface-variant">
            {groupSummary(nodes)}
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            "size-4 shrink-0 text-on-surface-muted transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open ? (
        <ul className="mt-1 flex flex-col gap-1 pl-4">
          {nodes.map((node) => (
            <OrderChainNode
              key={`${node.key}-${node.number ?? node.createdAt ?? ""}`}
              node={node}
              href={hrefOf(node)}
              onRetry={onRetry}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
